import { Card, CardHeader, CardTitle, CardBody, Chip, Divider } from '@vnetwork/ui/primitives';
import { EmptyState } from '@vnetwork/ui/data';
import { useContractUpdates, type ContractUpdateRow } from '@/api/contracts';
import { formatDateTime } from '@/lib/format';
import { humanize } from '@/pages/customers/detail/status-tones';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

/** One change-history entry: type chip, description, then who + when. */
function UpdateRow({ update }: { update: ContractUpdateRow }) {
  return (
    <div
      style={{
        display: 'grid',
        gap: 'var(--vn-space-1)',
        gridTemplateColumns: 'minmax(0, 1fr)',
        paddingBottom: 'var(--vn-space-3)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--vn-space-2)', flexWrap: 'wrap' }}>
        {update.contract_update_type ? (
          <Chip appearance="subtle" tone="info" size="sm">
            {humanize(update.contract_update_type)}
          </Chip>
        ) : null}
        <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
          {[update.employee_name, formatDateTime(update.created_at)].filter(Boolean).join(' · ')}
        </span>
      </div>
      {update.contract_update_description ? (
        <span style={{ fontSize: 'var(--vn-font-size-sm)', overflowWrap: 'anywhere' }}>
          {update.contract_update_description}
        </span>
      ) : null}
    </div>
  );
}

/**
 * Change-history log for a contract — a compact list of `contract_update`
 * entries (type + description + who/when). Rendered below the tabs.
 */
export function ContractUpdatesLog({ id }: { id: number }) {
  const { data, isLoading, isError, refetch } = useContractUpdates(id);
  const rows = data?.results ?? [];

  return (
    <Card flat>
      <CardHeader>
        <CardTitle>Activity Log</CardTitle>
      </CardHeader>
      <CardBody>
        {isError ? (
          <TabError title="Failed to load activity" onRetry={() => refetch()} />
        ) : isLoading ? (
          <TabLoading rows={3} />
        ) : rows.length === 0 ? (
          <EmptyState title="No activity" description="No changes recorded for this contract yet." />
        ) : (
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            {rows.map((update, i) => (
              <div key={i}>
                <UpdateRow update={update} />
                {i < rows.length - 1 && <Divider />}
              </div>
            ))}
          </div>
        )}
      </CardBody>
    </Card>
  );
}
